import { FORMAT, formatDateTime } from "Common/dateTimeUtils/format";
import { toEndOfDay, toStartOfDay } from "Common/dateTimeUtils/utils";
import { manageOverlay } from "Components/overlaysContext/overlaysManager";
import withOverlaysContext from "Components/overlaysContext/withOverlaysContext";
import classNames from "classnames";
import { Validator } from "devextreme-react";
import DateBox from "devextreme-react/date-box";
import filter from "lodash/filter";
import get from "lodash/get";
import isDate from "lodash/isDate";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import React, { memo, useEffect, useMemo, useRef } from "react";
import { v4 as uuid } from "uuid";
import locale from "./dateTimeWidget.locale";
function DateTimeWidget({ className, value, onChange, min, max, useTime, showAnalogClock, overlaysManager, getOverlaysContainer, width, validationRules, readOnly, disabled, zIndex, fieldName, withApplyButton, ...other }) {
    const id = useMemo(() => `picker_${uuid()}`, []);
    const ref = useRef(null);
    const validRef = useRef(true);
    const elementAttr = useMemo(() => ({
        class: classNames("hide-invalid-badge", className)
    }), [className]);
    const dropDownOptions = useMemo(() => ({
        wrapperAttr: {
            zIndex
        }
    }), [zIndex]);
    const getIsValid = (component) => { var _a; return Boolean(((_a = component === null || component === void 0 ? void 0 : component.instance()) === null || _a === void 0 ? void 0 : _a.option) && component.instance().option("isValid")); };
    useEffect(() => () => {
        manageOverlay(ref.current, overlaysManager, false);
    }, []);
    const handleApplyDateTime = (e) => {
        const { value: newValue, component, event } = e;
        if (!event || newValue === value)
            return;
        const timestamp = isDate(newValue) ? newValue.getTime() : +newValue;
        if (timestamp === value || !Number.isFinite(timestamp))
            return;
        validRef.current = getIsValid(component);
        onChange && onChange(timestamp, fieldName, validRef.current);
    };
    const handleInput = (e) => {
        if (get(e.event, "originalEvent.data")) {
            e.component.instance().repaint();
        }
    };
    const handleOpenedChanged = (opened) => {
        var _a;
        if (!opened) {
            const valid = getIsValid((_a = ref.current) === null || _a === void 0 ? void 0 : _a.instance);
            if (validRef.current !== valid) {
                validRef.current = valid;
                onChange && onChange(undefined, fieldName, valid);
            }
        }
        manageOverlay(ref.current, overlaysManager, opened);
    };
    const rules = useMemo(() => {
        if (!validationRules)
            return undefined;
        const result = filter(validationRules, r => r.type !== "range" || !isNil(r.min) || !isNil(r.max));
        if (isEmpty(result))
            return undefined;
        return result;
    }, [validationRules]);
    const now = Date.now();
    const dateTimeMin = min === "now" ? toStartOfDay(now) : min;
    const dateTimeMax = max === "now" ? toEndOfDay(now) : max;
    const dateOutOfRangeMessage = min !== undefined
        ? max !== undefined
            ? locale.buildMinMaxRestrictionsMessage(formatDateTime(dateTimeMin, FORMAT.DATE), formatDateTime(dateTimeMax, FORMAT.DATE))
            : locale.buildMinRestrictionsMessage(formatDateTime(dateTimeMin, FORMAT.DATE))
        : locale.buildMaxRestrictionsMessage(formatDateTime(dateTimeMax, FORMAT.DATE));
    return (React.createElement(DateBox, { id: id, elementAttr: elementAttr, ref: ref, value: isNil(value) || value === 0 || value === "" ? undefined : +value, width: width, useMaskBehavior: true, displayFormat: useTime ? FORMAT.DATE_TIME : FORMAT.DATE, type: useTime ? "datetime" : "date", onValueChanged: handleApplyDateTime, onInput: handleInput, min: dateTimeMin, max: dateTimeMax, validationMessageMode: "always", acceptCustomValue: false, dateOutOfRangeMessage: dateOutOfRangeMessage, deferRendering: false, showAnalogClock: showAnalogClock, onOpenedChange: handleOpenedChanged, readOnly: readOnly, disabled: disabled, dropDownOptions: dropDownOptions, applyValueMode: withApplyButton ? "useButtons" : "instantly", ...other },
        React.createElement(Validator, { validationRules: rules, onValidated: e => {
                const valid = Boolean(e.isValid);
                if (validRef.current !== valid) {
                    validRef.current = valid;
                    onChange && onChange(undefined, fieldName, valid);
                }
            } })));
}
export default memo(withOverlaysContext(DateTimeWidget));
DateTimeWidget.defaultProps = {
    useTime: false,
    width: "auto",
    invalidReset: true
};
